import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  createBranch,
  fetchBranch,
  fetchBranches,
  updateBranch,
} from '@main/features/branches/branchesSlice';
import {
  formatAddressForDisplay,
  formatTextFieldForInput,
} from '@main/features/branches/branchFieldFormat';
import { UiButton, UiInput, UiLinkButton, UiTextarea } from '@shared/common/ui';

export function BranchFormPage() {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNew = !branchId || branchId === 'new';

  const branch = useSelector((s) => (branchId ? s.branches.byId[branchId] : null));
  const [serverError, setServerError] = useState(null);

  const defaultValues = useMemo(
    () => ({
      name: branch?.name ?? '',
      address: formatAddressForDisplay(branch?.address),
      description: formatTextFieldForInput(branch?.description),
    }),
    [branch],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues });

  useEffect(() => {
    if (isNew) return;
    if (!branch) {
      dispatch(fetchBranch(branchId));
    } else {
      reset({
        name: branch.name ?? '',
        address: formatAddressForDisplay(branch.address),
        description: formatTextFieldForInput(branch.description),
      });
    }
  }, [dispatch, isNew, branchId, branch, reset]);

  const onSubmit = async (values) => {
    setServerError(null);
    const payload = {
      name: values.name.trim(),
      address: values.address.trim() || null,
      description: values.description.trim() || null,
    };

    const thunk = isNew
      ? createBranch(payload)
      : updateBranch({ branchId, ...payload });
    const result = await dispatch(thunk);
    if (result.meta.requestStatus === 'fulfilled') {
      dispatch(fetchBranches());
      navigate('/profile/branches');
    } else {
      setServerError(result.payload ?? 'Ошибка сохранения');
    }
  };

  return (
    <section>
      <div className="page-header">
        <h1>{isNew ? 'Новый филиал' : 'Редактирование филиала'}</h1>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-grid">
            <UiInput
              label="Название"
              style={{ gridColumn: '1 / -1' }}
              type="text"
              error={errors.name?.message}
              {...register('name', {
                required: 'Укажите название',
                maxLength: { value: 255, message: 'Не больше 255 символов' },
              })}
            />
            <UiInput
              label="Адрес"
              style={{ gridColumn: '1 / -1' }}
              type="text"
              error={errors.address?.message}
              {...register('address', {
                maxLength: { value: 500, message: 'Не больше 500 символов' },
              })}
            />
            <UiTextarea
              label="Описание"
              style={{ gridColumn: '1 / -1' }}
              rows={4}
              error={errors.description?.message}
              {...register('description', {
                maxLength: { value: 2000, message: 'Не больше 2000 символов' },
              })}
            />
          </div>

          {serverError && <div className="banner banner--danger">{serverError}</div>}

          <div className="form-actions">
            <UiLinkButton to="/profile/branches" variant="ghost">
              Отмена
            </UiLinkButton>
            <UiButton type="submit"  disabled={isSubmitting}>
              {isSubmitting ? 'Сохраняем…' : isNew ? 'Создать' : 'Сохранить'}
            </UiButton>
          </div>
        </form>
      </div>
    </section>
  );
}
